import { useRef, useCallback } from 'react';
import {
  parsePhoneNumberWithError,
  getCountries,
  CountryCode,
} from 'libphonenumber-js';

/**
 * Hook to enhance phone input autofill experience by automatically detecting and applying correct country codes
 *
 * Handles Safari's autofill bug where country codes are stripped, VPN scenarios where IP-based
 * country detection is incorrect, and provides smart phone pattern recognition.
 *
 * @param getSelectedCountryCode - Function that returns the currently selected country calling code (e.g., '+1', '+34')
 * @param onPhoneChange - Callback to trigger when phone value is enhanced
 * @returns Object with setupRef function to be used with ref callback
 */
export const usePhoneAutofillEnhancer = (
  getSelectedCountryCode: () => string,
  onPhoneChange?: (correctedValue: string) => void
) => {
  const lastValueRef = useRef<string>('');
  const countryCache = useRef<Map<string, string | null>>(new Map());
  const allCountriesRef = useRef<CountryCode[]>([]);

  // Initialize countries list once
  if (allCountriesRef.current.length === 0) {
    allCountriesRef.current = getCountries();
  }

  // Cache management - prevent unlimited growth
  const manageCacheSize = useCallback(() => {
    const maxCacheSize = 100; // Reasonable limit for phone number cache
    if (countryCache.current.size > maxCacheSize) {
      // Remove oldest entries (Map maintains insertion order)
      const entries = Array.from(countryCache.current.entries());
      const entriesToKeep = entries.slice(-50); // Keep last 50 entries
      countryCache.current.clear();
      entriesToKeep.forEach(([key, value]) => {
        countryCache.current.set(key, value);
      });
    }
  }, []);

  // Safe analytics tracking - only if available
  const trackPhoneInput = (
    eventName: string,
    properties: Record<string, any>
  ) => {
    try {
      // Send to New Relic via API endpoint
      fetch('/api/log', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          timestamp: new Date().toISOString(),
          level: 'info',
          message: eventName,
          attributes: {
            component: 'usePhoneAutofillEnhancer',
            ...properties,
          },
        }),
      }).catch(() => {
        // Silently fail - don't break the user experience
      });
    } catch {
      // New Relic not available - skip tracking
    }
  };

  /**
   * Get smart country list based on phone number patterns
   * Reduces the search space by filtering countries based on number length and starting digits
   */
  const getSmartCountryList = useCallback(
    (phoneNumber: string): CountryCode[] => {
      const cleanNumber = phoneNumber.replace(/\D/g, '');
      const numberLength = cleanNumber.length;

      // Priority countries based on actual user analytics data (top 20 countries by usage)
      const priorityCountries: CountryCode[] = [
        'RU',
        'US',
        'TR',
        'GB',
        'UA',
        'DE',
        'IN',
        'NL',
        'CA',
        'BY',
        'AE',
        'PL',
        'KZ',
        'ES',
        'FR',
        'AU',
        'BE',
        'PT',
        'IL',
        'PK',
      ];

      // Quick filters based on common patterns to reduce search space
      let candidateCountries: CountryCode[] = [];

      // Filter by number length (most countries have predictable ranges)
      if (numberLength >= 10 && numberLength <= 11) {
        // Common lengths for major countries
        candidateCountries = priorityCountries.slice(0, 10); // Top 10 priority
      } else if (numberLength >= 7 && numberLength <= 9) {
        // Shorter numbers, likely European or specific regions
        candidateCountries = ['GB', 'DE', 'FR', 'ES', 'NL', 'BE', 'PT'];
      } else if (numberLength >= 12) {
        // Longer numbers, often specific countries
        candidateCountries = ['IN', 'PK', 'AE'];
      }

      // Add all priority countries if we don't have enough candidates
      if (candidateCountries.length < 5) {
        const combined = candidateCountries.concat(priorityCountries);
        const uniqueSet = new Set(combined);
        candidateCountries = Array.from(uniqueSet);
      }

      // Fallback: if still no good candidates, use all countries but prioritize common ones
      if (candidateCountries.length === 0) {
        const remaining = allCountriesRef.current.filter(
          (c) => !priorityCountries.includes(c)
        );
        candidateCountries = priorityCountries.concat(remaining);
      }

      return candidateCountries;
    },
    []
  );

  /**
   * Detect correct country code based on phone number pattern
   * Uses caching and smart country filtering for performance
   */
  const detectCorrectCountryCode = useCallback(
    (phoneNumber: string): string | null => {
      if (!phoneNumber || phoneNumber.startsWith('+')) return null;

      // Check cache first
      const cached = countryCache.current.get(phoneNumber);
      if (cached !== undefined) {
        return cached;
      }

      // Manage cache size before adding new entries
      manageCacheSize();

      try {
        // Try to parse without country first (international format detection)
        try {
          const parsed = parsePhoneNumberWithError(phoneNumber);
          if (parsed?.isValid()) {
            const result = '+' + parsed.countryCallingCode;
            countryCache.current.set(phoneNumber, result);
            return result;
          }
        } catch (error) {
          // Expected for national format numbers without country - continue to country-specific parsing
        }

        // Get smart filtered country list based on number patterns
        const candidateCountries = getSmartCountryList(phoneNumber);

        // Try candidate countries in order
        for (const country of candidateCountries) {
          try {
            const parsed = parsePhoneNumberWithError(phoneNumber, country);
            if (parsed?.isValid()) {
              const result = '+' + parsed.countryCallingCode;
              countryCache.current.set(phoneNumber, result);
              return result;
            }
          } catch (error) {
            // Expected for numbers that don't match this country's format - continue to next country
            continue;
          }
        }

        // Cache miss result to avoid repeated expensive operations
        countryCache.current.set(phoneNumber, null);
      } catch (error) {
        // Unexpected error in the overall detection logic - report to analytics
        trackPhoneInput('Phone Autofill Enhancement Error', {
          error_type: 'country_detection_failure',
          phone_input: phoneNumber?.substring(0, 3) + '***', // Masked for privacy
          error_message:
            error instanceof Error ? error.message : 'Unknown error',
          user_agent: navigator.userAgent,
        });

        // Cache the failure to avoid repeated errors
        countryCache.current.set(phoneNumber, null);
      }

      return null;
    },
    [getSmartCountryList, trackPhoneInput, manageCacheSize]
  );

  // Event handlers
  const handleInput = useCallback(
    (event: Event) => {
      const target = event.target as HTMLInputElement;
      const currentValue = target.value;
      const lastValue = lastValueRef.current;

      // Detect potential Safari autofill:
      // 1. Value changed significantly (not just typing)
      // 2. New value doesn't start with country code
      // 3. New value looks like a phone number (7-15 digits)
      // 4. Value is different from last known value
      const cleanValue = currentValue.replace(/\D/g, '');
      const isLikelyAutofill =
        currentValue !== lastValue &&
        !currentValue.startsWith('+') &&
        cleanValue.length >= 7 &&
        cleanValue.length <= 15 &&
        /^\d/.test(currentValue);

      if (isLikelyAutofill) {
        const detectedCountryCode = detectCorrectCountryCode(currentValue);
        const selectedCountryCode = getSelectedCountryCode();

        // Use detected country code if different from selected (VPN case)
        const correctCountryCode = detectedCountryCode || selectedCountryCode;

        if (correctCountryCode && correctCountryCode !== '+') {
          const correctedValue = correctCountryCode + ' ' + currentValue;

          // Update the input value
          target.value = correctedValue;

          // Trigger change event for form validation
          target.dispatchEvent(new Event('change', { bubbles: true }));

          // Call the change handler if provided
          onPhoneChange?.(correctedValue);

          console.log(
            '[Phone Autofill Enhancer] Enhanced phone:',
            currentValue,
            '->',
            correctedValue
          );

          if (
            detectedCountryCode &&
            detectedCountryCode !== selectedCountryCode
          ) {
            console.log(
              '[Phone Autofill Enhancer] VPN detected - used phone-based country code:',
              detectedCountryCode
            );

            // Track VPN scenario for analytics
            trackPhoneInput('Phone Autofill VPN Detection', {
              selected_country_code: selectedCountryCode,
              detected_country_code: detectedCountryCode,
              phone_pattern: currentValue,
              user_agent: navigator.userAgent,
            });
          }
        }
      }

      lastValueRef.current = target.value;
    },
    [
      getSelectedCountryCode,
      onPhoneChange,
      detectCorrectCountryCode,
      trackPhoneInput,
    ]
  );

  const handleBlur = useCallback(
    (event: Event) => {
      const target = event.target as HTMLInputElement;
      const currentValue = target.value;

      // Additional check on blur - catch any missed autofill scenarios
      if (
        currentValue &&
        !currentValue.startsWith('+') &&
        /^\d/.test(currentValue)
      ) {
        const detectedCountryCode = detectCorrectCountryCode(currentValue);
        const selectedCountryCode = getSelectedCountryCode();
        const correctCountryCode = detectedCountryCode || selectedCountryCode;

        if (correctCountryCode && correctCountryCode !== '+') {
          const correctedValue = correctCountryCode + ' ' + currentValue;
          target.value = correctedValue;
          target.dispatchEvent(new Event('change', { bubbles: true }));
          onPhoneChange?.(correctedValue);

          console.log(
            '[Phone Autofill Enhancer] Blur-enhanced phone:',
            currentValue,
            '->',
            correctedValue
          );
        }
      }
    },
    [getSelectedCountryCode, onPhoneChange, detectCorrectCountryCode]
  );

  // Function to set up the ref and event listeners
  const setupRef = useCallback(
    (element: HTMLInputElement | null) => {
      if (!element) return;

      // Only apply enhancement on Safari
      const isSafari = /^((?!chrome|android).)*safari/i.test(
        navigator.userAgent
      );
      if (!isSafari) return;

      // Add event listeners
      element.addEventListener('input', handleInput);
      element.addEventListener('blur', handleBlur);

      // Return cleanup function
      return () => {
        element.removeEventListener('input', handleInput);
        element.removeEventListener('blur', handleBlur);
      };
    },
    [handleInput, handleBlur]
  );

  return { setupRef };
};
